import { type Address, dustThreshold } from './address';
import type { OutPoint } from './tx';
import { MAX_MONEY } from './payment';

/** A coin the wallet can spend (from /v1/wallet/utxos). */
export interface Coin {
  outpoint: OutPoint;
  value: number;
  scriptPubKey: Uint8Array;
  /** 0 = receive chain, 1 = change chain. */
  chain: number;
  index: number;
  confirmations: number;
}

export type SendError =
  | { kind: 'invalidAmount' }
  | { kind: 'amountBelowDust'; minimum: number }
  | { kind: 'insufficientFunds'; available: number; needed: number }
  | { kind: 'feeTooHigh'; fee: number };

export class SendFailure extends Error {
  constructor(public readonly reason: SendError) {
    super(reason.kind);
  }
}

/** What will be sent, before signing. Shown to the user for confirmation. */
export interface SendPlan {
  coins: Coin[];
  recipient: Address;
  amount: number;
  fee: number;
  /** 0 when there is no change output. */
  change: number;
  changeIndex: number | null;
  feeRate: number;
  estimatedVSize: number;
}

/** Refuse fees above this fraction of the amount (protects against a bad fee rate or a typo). */
export const MAX_FEE_FRACTION = 0.25;
/** Absolute fee sanity cap: 0.1 CW. */
export const MAX_ABSOLUTE_FEE = 10_000_000;

const OVERHEAD_WU = 42; // version, locktime, counts, segwit marker + flag
const P2WPKH_INPUT_WU = 272; // low-R signatures, ≤ 72 bytes with sighash type
const outputWU = (script: Uint8Array) => 4 * (8 + 1 + script.length);

export function estimateVSize(inputs: number, outputScripts: Uint8Array[]): number {
  const wu = OVERHEAD_WU + inputs * P2WPKH_INPUT_WU + outputScripts.reduce((n, s) => n + outputWU(s), 0);
  return Math.ceil(wu / 4);
}

const feeFor = (vsize: number, rate: number) => Math.ceil(vsize * rate);

function checkFee(fee: number, amount: number) {
  if (fee > MAX_ABSOLUTE_FEE || (fee > amount * MAX_FEE_FRACTION && fee > 100_000)) {
    throw new SendFailure({ kind: 'feeTooHigh', fee });
  }
}

/**
 * Chooses coins and computes the fee and change (same algorithm as the iOS app).
 * @param amount satoshis, or null to send everything ("Max").
 * @param feeRate sat/vB; values below 1 are raised to 1.
 * @param subtractFee take the fee out of `amount`: the wallet is debited exactly `amount` and the
 *   recipient gets `amount - fee`. Ignored for "Max", which already works that way.
 */
export function planSend(
  coins: Coin[],
  recipient: Address,
  amount: number | null,
  feeRate: number,
  changeScript: Uint8Array,
  changeIndex: number,
  subtractFee = false,
): SendPlan {
  const rate = Math.max(feeRate, 1);
  const spendable = coins
    .filter((c) => c.value > 0)
    .sort((a, b) => {
      const ca = a.confirmations > 0 ? 1 : 0;
      const cb = b.confirmations > 0 ? 1 : 0;
      return cb - ca || b.value - a.value;
    });
  const available = spendable.reduce((n, c) => n + c.value, 0);
  const dust = dustThreshold(recipient.scriptPubKey);

  if (amount === null) {
    if (spendable.length === 0) throw new SendFailure({ kind: 'insufficientFunds', available: 0, needed: dust });
    const size = estimateVSize(spendable.length, [recipient.scriptPubKey]);
    const f = feeFor(size, rate);
    const net = available - f;
    if (net < dust) throw new SendFailure({ kind: 'insufficientFunds', available, needed: f + dust });
    checkFee(f, net);
    return { coins: spendable, recipient, amount: net, fee: f, change: 0, changeIndex: null, feeRate: rate, estimatedVSize: size };
  }

  if (!Number.isSafeInteger(amount) || amount <= 0 || amount > MAX_MONEY) throw new SendFailure({ kind: 'invalidAmount' });
  if (amount < dust) throw new SendFailure({ kind: 'amountBelowDust', minimum: dust });

  const selected: Coin[] = [];
  let total = 0;
  const changeDust = dustThreshold(changeScript);

  if (subtractFee) {
    for (const coin of spendable) {
      selected.push(coin);
      total += coin.value;
      if (total < amount) continue;
      const sizeWithChange = estimateVSize(selected.length, [recipient.scriptPubKey, changeScript]);
      const feeWithChange = feeFor(sizeWithChange, rate);
      const change = total - amount;
      if (change >= changeDust) {
        const net = amount - feeWithChange;
        if (net < dust) throw new SendFailure({ kind: 'amountBelowDust', minimum: dust + feeWithChange });
        checkFee(feeWithChange, net);
        return { coins: [...selected], recipient, amount: net, fee: feeWithChange, change, changeIndex, feeRate: rate, estimatedVSize: sizeWithChange };
      }
      // Leftover below dust: no change output, it goes to the fee.
      const sizeNoChange = estimateVSize(selected.length, [recipient.scriptPubKey]);
      const feeNoChange = feeFor(sizeNoChange, rate);
      const net = amount - feeNoChange;
      if (net < dust) throw new SendFailure({ kind: 'amountBelowDust', minimum: dust + feeNoChange });
      const actualFee = total - net;
      checkFee(actualFee, net);
      return { coins: [...selected], recipient, amount: net, fee: actualFee, change: 0, changeIndex: null, feeRate: rate, estimatedVSize: sizeNoChange };
    }
    throw new SendFailure({ kind: 'insufficientFunds', available, needed: amount });
  }

  for (const coin of spendable) {
    selected.push(coin);
    total += coin.value;
    const sizeWithChange = estimateVSize(selected.length, [recipient.scriptPubKey, changeScript]);
    const feeWithChange = feeFor(sizeWithChange, rate);
    const change = total - amount - feeWithChange;
    if (change >= changeDust) {
      checkFee(feeWithChange, amount);
      return { coins: [...selected], recipient, amount, fee: feeWithChange, change, changeIndex, feeRate: rate, estimatedVSize: sizeWithChange };
    }
    // Without change: the leftover (below dust) goes to the fee.
    const sizeNoChange = estimateVSize(selected.length, [recipient.scriptPubKey]);
    const feeNoChange = feeFor(sizeNoChange, rate);
    if (total >= amount + feeNoChange) {
      const actualFee = total - amount;
      checkFee(actualFee, amount);
      return { coins: [...selected], recipient, amount, fee: actualFee, change: 0, changeIndex: null, feeRate: rate, estimatedVSize: sizeNoChange };
    }
  }
  const needed = amount + feeFor(estimateVSize(Math.max(selected.length, 1), [recipient.scriptPubKey]), rate);
  throw new SendFailure({ kind: 'insufficientFunds', available, needed });
}
